<template>
    <div class="live-domains-page">
        <Page>
            <template #title>
                <h1>{{ stekName }} - All Domains</h1>
            </template>
            <template #default>
                <div v-if="loading" class="loading">
                    <Spinner />
                </div>
                <div v-else-if="error" class="error">
                    <Message severity="error">{{ error }}</Message>
                </div>
                <div v-else class="domains-grid">
                    <Card
                        v-for="domain in domains"
                        :key="domain.identity"
                        class="domain-card"
                        @click="navigateToDomain(domain.identity)"
                    >
                        <template #title>{{
                            domain.name || domain.identity
                        }}</template>
                        <template #content>
                            <div v-if="domain.metrics" class="metrics-preview">
                                <div class="metric">
                                    <span class="metric-label"
                                        >Total Events:</span
                                    >
                                    <span class="metric-value">{{
                                        domain.metrics.total_events
                                    }}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label"
                                        >Error Rate:</span
                                    >
                                    <span class="metric-value"
                                        >{{
                                            domain.metrics.error_rate.toFixed(
                                                2
                                            )
                                        }}%</span
                                    >
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </template>
        </Page>
    </div>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Message from "primevue/message";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import Page from "@/components/Page.vue";
import { useStekManager } from "@/composables/stek-manager";
import { useClient, useServices } from "@/lib/openapi";
import type { LiveMetrics } from "@/lib/openapi/gen/types.gen";
import Spinner from "@/shared/Spinner.vue";

defineOptions({
    name: "DomainsPage",
});

const route = useRoute();
const router = useRouter();
const { stek } = useStekManager();
const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});
const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const domains = ref<
    Array<{ identity: string; name?: string; metrics?: LiveMetrics }>
>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const stekName = computed(() => {
    return stek.value?.name || route.params.stekName;
});

async function fetchDomains() {
    loading.value = true;
    error.value = null;
    try {
        const domainsResult =
            await api.startDomainDomainMgtGetDomainsByStekStartPost();
        if (domainsResult.response.ok && domainsResult.data) {
            let domainsData: Array<Record<string, unknown>> = [];
            if (Array.isArray(domainsResult.data)) {
                domainsData = domainsResult.data;
            } else if (
                domainsResult.data &&
                Array.isArray(domainsResult.data.items)
            ) {
                domainsData = domainsResult.data.items;
            }

            // Fetch metrics for each domain
            const domainsWithMetrics = await Promise.all(
                domainsData.map(async (d: Record<string, unknown>) => {
                    try {
                        const response = await client.post<LiveMetrics>({
                            url: "/stek/live_mgt/get_metrics/start",
                            body: {
                                domain_id: d.identity || d.id || String(d),
                                hours: 24,
                            },
                        });

                        if (!response.response.ok || !response.data) {
                            return {
                                identity: d.identity || d.id || String(d),
                                name: d.name || d.title,
                            };
                        }

                        const metrics = response.data;
                        return {
                            identity: d.identity || d.id || String(d),
                            name: d.name || d.title,
                            metrics,
                        };
                    } catch {
                        return {
                            identity: d.identity || d.id || String(d),
                            name: d.name || d.title,
                        };
                    }
                })
            );
            domains.value = domainsWithMetrics;
        }
    } catch (e) {
        error.value = e instanceof Error ? e.message : "Failed to load domains";
    } finally {
        loading.value = false;
    }
}

function navigateToDomain(domainId: string) {
    const orgName = route.params.orgName;
    const stekName = route.params.stekName;
    router.push({
        name: "liveDomainPage",
        params: { orgName, stekName, domainId },
    });
}

onMounted(() => {
    fetchDomains();
});
</script>

<style scoped lang="scss">
.live-domains-page {
    padding: 1rem;
}

.domains-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
}

.domain-card {
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.metrics-preview {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.metric {
    display: flex;
    justify-content: space-between;
}

.metric-label {
    font-weight: 500;
}

.metric-value {
    font-weight: bold;
}

.loading,
.error {
    text-align: center;
    padding: 2rem;
}
</style>
